import { Component, OnInit } from '@angular/core';
import { UnifiedCartService } from '../../services/unified-cart/unified-cart.service';
import { AuthSessionService } from '../../services/auth-session/auth-session.service';
import { Router } from '@angular/router';

/* Start Footer Link Model Section */
interface EcbFooterLink {
  label: string;
  url: string;
  queryParams?: Record<string, string>;
}

/* End Footer Link Model Section */
@Component({
  selector: 'app-public-layout',
  templateUrl: './public-layout.component.html',
  styleUrls: ['./public-layout.component.scss']
})
export class PublicLayoutComponent implements OnInit {
  // Number of unique courses in the cart.
  cartCount = 0;



  // Constructor with cart/session dependencies.
  constructor(private cartService: UnifiedCartService, public session: AuthSessionService, private router: Router) { }

  // Load page data.
  loadPageData(): void {
    // Watch cart changes for the navbar badge.
    this.cartService.items$.subscribe(items => this.cartCount = items.length);
  }


  // Component init.
  ngOnInit(): void {
    // Load page data.
    this.loadPageData();
  }

  isRegularShortCourseActive(): boolean {
    const urlTree = this.router.parseUrl(this.router.url);

    const path = urlTree.root.children['primary']?.segments
      .map(segment => segment.path)
      .join('/') ?? '';

    const type = urlTree.queryParams['type'];

    const validTypes = ['ALL', 'UOC', 'OSC', 'HRC'];
    const validPaths = ['shortcourselist', 'shortcoursedetails'];

    return validTypes.includes(type) && validPaths.includes(path);
  }
  /* Start Footer Year Property Section */
  public readonly currentYear = new Date().getFullYear();
  /* End Footer Year Property Section */

  /* Start Footer Course Link Section */
  public readonly courseLinks: EcbFooterLink[] = [
    { label: 'Qualifications', url: '/course-qualifications' },
    { label: 'Short Courses', url: '/course-short', queryParams: { type: 'all_courses' } },
    { label: 'High Risk Courses', url: '/course-short', queryParams: { type: 'hrc' } },
    { label: 'Skill Sets', url: '/course-short', queryParams: { type: 'ss' } },
    { label: 'VOC Courses', url: '/course-voc' },
    { label: 'HSR Initial OHS Training', url: '/hsr-training' },
  ];
  /* End Footer Course Link Section */

  /* Start Footer Quick Link Section */
  public readonly quickLinks: EcbFooterLink[] = [
    { label: 'Locations', url: '/contact-us' , queryParams: { type: 'location' } },
    { label: 'About Us', url: '/about-us' },
    { label: 'Contact Us', url: '/contact-us' },
    { label: 'Terms & Conditions', url: '#' },
    
  ];
  /* End Footer Quick Link Section */
 


  // Control whether the login dropdown is visible.
  public showLoginDropdown: boolean = false;
  // Control whether the mobile navigation is visible.
  public showMobileNavigation: boolean = false;
  // Control whether the desktop Training dropdown is visible.
  public showTrainingDropdown: boolean = false;
  // Control whether the desktop Community dropdown is visible.
  public showCommunityDropdown: boolean = false;
  // Control whether the mobile Training sub navigation is visible.
  public showMobileTrainingSubNavigation: boolean = false;
  // Control whether the mobile Community sub navigation is visible.
  public showMobileCommunitySubNavigation: boolean = false;


  /* End Property Declaration Section */

  /* Start Header Menu Property Section */
  public showMobileMenu: boolean = false;

  /* End Header Menu Property Section */

  /* Start Login Dropdown Toggle Function Section */
  toggleLoginDropdown(): void {
    this.showLoginDropdown = !this.showLoginDropdown;
    this.showTrainingDropdown = false;
    this.showCommunityDropdown = false;
    this.showMobileNavigation = false;
  }
  /* End Login Dropdown Toggle Function Section */


  /* Start Login Dropdown Close Function Section */
  closeLoginDropdown(): void {
    this.showLoginDropdown = false;
  }
  /* End Login Dropdown Close Function Section */


  /* Start Mobile Navigation Toggle Function Section */
  toggleMobileNavigation(): void {
    this.showMobileNavigation = !this.showMobileNavigation;
    this.showLoginDropdown = false;
    this.showTrainingDropdown = false;
    this.showCommunityDropdown = false;
    if (!this.showMobileNavigation) {
      this.showMobileTrainingSubNavigation = false;
      this.showMobileCommunitySubNavigation = false;
    }
  }
  /* End Mobile Navigation Toggle Function Section */


  /* Start Mobile Navigation Close Function Section */
  closeMobileNavigation(): void {
    this.showMobileNavigation = false;
  }
  /* End Mobile Navigation Close Function Section */


  /* Start Header Menu Close Function Section */
  closeHeaderMenus(): void {
    this.showLoginDropdown = false;
    this.showTrainingDropdown = false;
    this.showCommunityDropdown = false;
    this.showMobileNavigation = false;
    this.showMobileTrainingSubNavigation = false;
    this.showMobileCommunitySubNavigation = false;
  }
  /* End Header Menu Close Function Section */




  /* Start Training Dropdown Toggle Function Section */
  toggleTrainingDropdown(): void {
    // Toggle the Training dropdown.
    this.showTrainingDropdown = !this.showTrainingDropdown;
    // Close the Community dropdown so both menus are not open together.
    this.showCommunityDropdown = false;
    // Close the login dropdown when Training is opened.
    this.showLoginDropdown = false;
  }
  /* End Training Dropdown Toggle Function Section */


  /* Start Community Dropdown Toggle Function Section */
  toggleCommunityDropdown(): void {
    // Toggle the Community dropdown.
    this.showCommunityDropdown = !this.showCommunityDropdown;
    // Close the Training dropdown so both menus are not open together.
    this.showTrainingDropdown = false;
    // Close the login dropdown when Community is opened.
    this.showLoginDropdown = false;
  }
  /* End Community Dropdown Toggle Function Section */

  /* Start Mobile Training Sub Navigation Toggle Function Section */
  toggleMobileTrainingSubNavigation(): void {
    // Toggle the Training sub navigation.
    this.showMobileTrainingSubNavigation = !this.showMobileTrainingSubNavigation;
    // Close the Community mobile sub navigation.
    this.showMobileCommunitySubNavigation = false;
  }
  /* End Mobile Training Sub Navigation Toggle Function Section */


  /* Start Mobile Community Sub Navigation Toggle Function Section */
  toggleMobileCommunitySubNavigation(): void {
    // Toggle the Community sub navigation.
    this.showMobileCommunitySubNavigation = !this.showMobileCommunitySubNavigation;
    // Close the Training mobile sub navigation.
    this.showMobileTrainingSubNavigation = false;
  }
  /* End Mobile Community Sub Navigation Toggle Function Section */

  /* Start Training Section Active Function Section */
  isTrainingSectionActive(): boolean {
    return ['/training', '/hsr-training']
      .some(path => this.router.url === path || this.router.url.startsWith(`${path}?`) || this.router.url.startsWith(`${path}#`));
  }
  /* End Training Section Active Function Section */


  /* Start Community Section Active Function Section */
  isCommunitySectionActive(): boolean {
    // Mark Community as active for either supported Community route.
    return ['/breaking-the-cycle', '/community-services']
      .some(path => this.router.url === path || this.router.url.startsWith(`${path}?`) || this.router.url.startsWith(`${path}#`));
  }
  /* End Community Section Active Function Section */

  /* Start Footer Link Track By Function Section */
  trackByLabel(
    index: number,
    link: EcbFooterLink
  ): string {
    return link.label;
  }
  /* End Footer Link Track By Function Section */
}
